import {Button, Group, Modal, Stack, Text} from '@mantine/core'
import {useEffect, useState} from 'react'

import type {GoogleMapsSource} from './google-maps/google-maps-metrics'
import type {GoogleMapsProviderStatus} from './google-maps/google-maps-provider'
import {LocationEditForm} from './location-edit-form'
import {LocationMapPicker} from './location-map-picker'
import {validateLocation} from './location-validation'
import {createEmptyLocation, type JobLocation} from './types'

type LocationEditMode = 'create-new' | 'select-saved' | 'edit'

type LocationEditModalProps = {
    isOpen: boolean
    onClose: () => void
    onSave: (location: JobLocation) => void | Promise<void>
    mode: LocationEditMode
    source: GoogleMapsSource
    initialLocation?: JobLocation
    customerId: string | null
    MapPickerComponent?: typeof LocationMapPicker
    showSaveCheckbox?: boolean
    title?: string
    nameLabel?: string
    showNotes?: boolean
    enableProviderFallback?: boolean
    mapProviderStatus?: GoogleMapsProviderStatus
}

export function LocationEditModal({
    isOpen,
    onClose,
    onSave,
    mode,
    source,
    initialLocation,
    customerId,
    MapPickerComponent = LocationMapPicker,
    showSaveCheckbox = true,
    title,
    nameLabel,
    showNotes,
    enableProviderFallback = false,
    mapProviderStatus,
}: LocationEditModalProps) {
    const [location, setLocation] = useState<JobLocation>(initialLocation ?? createEmptyLocation())
    const [saving, setSaving] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [touched, setTouched] = useState({name: false, position: false})
    const [triedSave, setTriedSave] = useState(false)

    // Reset location when modal opens or initialLocation changes
    useEffect(() => {
        if (isOpen) {
            if (initialLocation) {
                setLocation(initialLocation)
            } else {
                setLocation({
                    ...createEmptyLocation(),
                    savedLocationMode: mode,
                })
            }
            setErrorMessage(null)
            setTouched({name: false, position: false})
            setTriedSave(false)
        }
    }, [isOpen, initialLocation, mode])

    function handleUpdate(partial: Partial<JobLocation>) {
        setLocation({...location, ...partial})
        setErrorMessage(null)
    }

    function handleClose() {
        if (saving) {
            return
        }
        onClose()
    }

    async function handleSave() {
        if (!validateLocation(location, nameLabel).isValid) {
            setTriedSave(true)
            return
        }

        setSaving(true)
        setErrorMessage(null)
        try {
            await onSave(location)
            onClose()
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to save location')
        } finally {
            setSaving(false)
        }
    }

    const resolvedTitle =
        title ??
        (mode === 'edit'
            ? 'Edit location'
            : mode === 'select-saved'
              ? 'Select saved location'
              : 'Add location')

    const validation = validateLocation(location, nameLabel)
    const nameError = triedSave || touched.name ? validation.nameError : undefined
    const latitudeError = triedSave || touched.position ? validation.latitudeError : undefined

    return (
        <Modal
            opened={isOpen}
            onClose={handleClose}
            title={resolvedTitle}
            size="xl"
            closeOnClickOutside={!saving}
            closeOnEscape={!saving}
        >
            <Stack gap="md">
                {errorMessage ? (
                    <Text size="sm" c="red">
                        {errorMessage}
                    </Text>
                ) : null}

                <LocationEditForm
                    location={location}
                    mode={mode}
                    source={source}
                    customerId={customerId}
                    nameError={nameError}
                    latitudeError={latitudeError}
                    onUpdate={handleUpdate}
                    onNameBlur={() => setTouched((current) => ({...current, name: true}))}
                    onPositionBlur={() => setTouched((current) => ({...current, position: true}))}
                    MapPickerComponent={MapPickerComponent}
                    addressAutocompleteKey="modal-location"
                    showSaveCheckbox={showSaveCheckbox}
                    nameLabel={nameLabel}
                    showNotes={showNotes}
                    enableProviderFallback={enableProviderFallback}
                    mapProviderStatus={mapProviderStatus}
                />

                <Group justify="flex-end" gap="xs">
                    <Button variant="subtle" onClick={handleClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => void handleSave()}
                        disabled={
                            saving || (mode === 'select-saved' && !location.fromSavedLocationId)
                        }
                        loading={saving}
                    >
                        Save
                    </Button>
                </Group>
            </Stack>
        </Modal>
    )
}
